import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import {
  Network, ChevronDown, ChevronRight, Building2, Search,
  Expand, Minimize, Download, LayoutList, GitBranch, Plus, Minus,
} from "lucide-react";
import EmployeeProfileModal from "../components/EmployeeProfileModal";

/* ─────────── helpers ─────────── */

function buildHierarchy(users) {
  const byId = {};
  users.forEach((u) => { byId[u.id] = { ...u, _reportIds: [] }; });
  // Only exclude the SYSTEM admin (no manager, no department) — real people
  // with role "admin" (e.g. COO) must appear in the org chart.
  const isSystemAdmin = (u) => u.role === "admin" && !u.manager_id && !u.department_id;
  const roots = [];
  users.forEach((u) => {
    if (isSystemAdmin(u)) return;
    if (u.manager_id && byId[u.manager_id] && !isSystemAdmin(byId[u.manager_id])) {
      byId[u.manager_id]._reportIds.push(u.id);
    } else {
      roots.push(byId[u.id]);
    }
  });
  // CEO first; rest alphabetical
  roots.sort((a, b) => (a.role === "ceo" ? -1 : b.role === "ceo" ? 1 : a.name.localeCompare(b.name)));
  // Sort each level's children alphabetically by name
  const sortChildren = (n) => {
    n._reportIds.sort((a, b) => byId[a].name.localeCompare(byId[b].name));
    n._reportIds.forEach((id) => sortChildren(byId[id]));
  };
  roots.forEach(sortChildren);
  return { byId, roots };
}

function collectMatchingIds(node, byId, q) {
  if (!q) return new Set();
  const out = new Set();
  const walk = (n, ancestors) => {
    const text = `${n.name} ${n.job_title || ""} ${n.email || ""}`.toLowerCase();
    const isMatch = text.includes(q);
    if (isMatch) {
      out.add(n.id);
      ancestors.forEach((a) => out.add(a));
    }
    n._reportIds.forEach((cid) => walk(byId[cid], [...ancestors, n.id]));
  };
  walk(node, []);
  return out;
}

/* ─────────── List view (legacy) ─────────── */

function flattenTree(roots, byId, expandedMap, search, matchSet, depth, out) {
  for (const node of roots) {
    if (search && !matchSet.has(node.id)) continue;
    const isExpanded = expandedMap[node.id] !== false;
    out.push({ node, depth, isExpanded, hasReports: node._reportIds.length > 0 });
    if (isExpanded && node._reportIds.length > 0) {
      const children = node._reportIds.map((id) => byId[id]).filter(Boolean);
      flattenTree(children, byId, expandedMap, search, matchSet, depth + 1, out);
    }
  }
}

/* ─────────── Tree view (iterative bottom-up rendering — avoids self-recursive JSX) ─────────── */

function renderTreeNodeCard(node, depts, expandedMap, hasChildren, isExpanded, toggle, onSelect, search) {
  const dept = depts.find((d) => d.id === node.department_id);
  const initials = node.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
  const isHighlight = !!search &&
    `${node.name} ${node.job_title || ""} ${node.email || ""}`.toLowerCase().includes(search);
  const cls = `sw-tree-card ${node.role === "ceo" ? "is-ceo" : ""} ${isHighlight ? "is-highlight" : ""}`;
  const childrenEl = [
    React.createElement("div", { className: "sw-tree-avatar", key: "av" }, initials),
    React.createElement("div", { className: "sw-tree-name", key: "nm" }, node.name),
    React.createElement("div", { className: "sw-tree-title", key: "tt" }, node.job_title || "—"),
  ];
  if (dept) childrenEl.push(React.createElement("div", { className: "sw-tree-dept", key: "dp" }, dept.name));
  if (hasChildren) {
    childrenEl.push(React.createElement("span", { className: "sw-tree-count", "data-testid": `tree-count-${node.id}`, key: "ct" }, node._reportIds.length));
    childrenEl.push(React.createElement("button", {
      type: "button",
      className: "sw-tree-toggle",
      "data-testid": `tree-toggle-${node.id}`,
      onClick: (e) => { e.stopPropagation(); toggle(node.id); },
      "aria-label": isExpanded ? "Collapse" : "Expand",
      key: "tg",
    }, isExpanded ? React.createElement(Minus, { size: 12 }) : React.createElement(Plus, { size: 12 })));
  }
  return React.createElement("div", {
    className: cls,
    "data-testid": `tree-node-${node.id}`,
    onClick: () => onSelect(node),
  }, childrenEl);
}

function renderTree({ roots, byId, depts, expandedMap, toggle, onSelect, search, matchSet }) {
  // Bottom-up DFS to build per-node rendered <li>… element.
  const rendered = {};
  const stack = roots.filter((r) => !search || matchSet.has(r.id)).map((r) => ({ node: r, visited: false }));
  while (stack.length) {
    const top = stack[stack.length - 1];
    if (!top.visited) {
      top.visited = true;
      const children = top.node._reportIds
        .map((id) => byId[id])
        .filter((c) => !search || matchSet.has(c.id));
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ node: children[i], visited: false });
      }
    } else {
      stack.pop();
      const node = top.node;
      const isExpanded = expandedMap[node.id] !== false;
      const visibleChildren = node._reportIds
        .map((id) => byId[id])
        .filter((c) => !search || matchSet.has(c.id));
      const hasChildren = visibleChildren.length > 0;
      const showChildren = hasChildren && isExpanded;
      const card = renderTreeNodeCard(node, depts, expandedMap, hasChildren, isExpanded, toggle, onSelect, search);
      const wrapper = React.createElement(
        "div",
        { className: `sw-tree-node ${showChildren ? "has-children" : ""}` },
        card,
      );
      const liChildren = [wrapper];
      if (showChildren) {
        const childrenAreSingle = visibleChildren.length === 1;
        const ul = React.createElement(
          "ul",
          { className: childrenAreSingle ? "sw-tree-single" : "", key: "ul" },
          visibleChildren.map((c) => rendered[c.id]),
        );
        liChildren.push(ul);
      }
      rendered[node.id] = React.createElement("li", { key: node.id }, liChildren);
    }
  }
  const visibleRoots = roots.filter((r) => !search || matchSet.has(r.id));
  return React.createElement(
    "ul",
    { className: visibleRoots.length === 1 ? "sw-tree-single" : "" },
    visibleRoots.map((r) => rendered[r.id]),
  );
}

/* ─────────── Page ─────────── */

export default function OrgChart() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [expandedMap, setExpandedMap] = useState({});
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("tree"); // 'tree' | 'list'
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    api.get("/orgchart").then((r) => {
      setData(r.data);
      // Default expansion: open the CEO/roots + everyone within 2 levels so
      // the tree isn't just a lone CEO card on first paint. Users can still
      // click Collapse-all to zoom out.
      const m = {};
      const byId = {};
      (r.data.users || []).forEach((u) => { byId[u.id] = u; });
      const expandLevel = (uid, depth) => {
        if (depth > 2) return;
        m[uid] = true;
        (r.data.users || []).forEach((c) => {
          if (c.manager_id === uid) expandLevel(c.id, depth + 1);
        });
      };
      (r.data.users || []).forEach((u) => {
        const isSysAdmin = u.role === "admin" && !u.manager_id && !u.department_id;
        if (!isSysAdmin && (!u.manager_id || !byId[u.manager_id])) {
          expandLevel(u.id, 0);
        }
      });
      setExpandedMap(m);
    });
  }, []);

  const built = useMemo(() => data ? buildHierarchy(data.users) : null, [data]);

  if (!data || !built) return <div>{t("common.loading")}</div>;
  const { byId, roots } = built;

  const q = (search || "").trim().toLowerCase();
  const matchSet = q
    ? roots.reduce((acc, r) => {
        collectMatchingIds(r, byId, q).forEach((id) => acc.add(id));
        return acc;
      }, new Set())
    : new Set();

  const toggle = (id) => setExpandedMap({ ...expandedMap, [id]: expandedMap[id] === false ? true : false });
  const expandAll = () => {
    const m = {};
    data.users.forEach((u) => { m[u.id] = true; });
    setExpandedMap(m);
  };
  const collapseAll = () => {
    const m = {};
    data.users.forEach((u) => { m[u.id] = false; });
    setExpandedMap(m);
  };

  const flatList = [];
  if (view === "list") {
    flattenTree(roots, byId, expandedMap, q, matchSet, 0, flatList);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Network size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("org.title")}</h1>
            <p className="text-[14px] text-[#747474]">
              {data.users.filter((u) => !(u.role === "admin" && !u.manager_id && !u.department_id)).length} {t("org.subtitle_people")} · {data.departments.length} {t("org.subtitle_depts")}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* View toggle */}
          <div className="inline-flex rounded-[10px] border border-[#cbd5e1] bg-white p-0.5" data-testid="orgchart-view-toggle">
            <button
              data-testid="orgchart-view-tree"
              onClick={() => setView("tree")}
              className={`px-3 py-1.5 text-[12px] rounded-[8px] flex items-center gap-1.5 ${view === "tree" ? "bg-[#475569] text-white" : "text-[#475569]"}`}
            >
              <GitBranch size={13} /> {t("org.view_tree")}
            </button>
            <button
              data-testid="orgchart-view-list"
              onClick={() => setView("list")}
              className={`px-3 py-1.5 text-[12px] rounded-[8px] flex items-center gap-1.5 ${view === "list" ? "bg-[#475569] text-white" : "text-[#475569]"}`}
            >
              <LayoutList size={13} /> {t("org.view_list")}
            </button>
          </div>
          <button data-testid="expand-all-btn" onClick={expandAll} className="sw-btn-secondary"><Expand size={14} /> {t("org.expand_all")}</button>
          <button data-testid="collapse-all-btn" onClick={collapseAll} className="sw-btn-secondary"><Minimize size={14} /> {t("org.collapse_all")}</button>
          <button
            data-testid="export-pdf-btn"
            onClick={async () => {
              try {
                const r = await api.get("/org-chart/pdf", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = "sagewood-org-chart.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) { alert(t("org.export_failed")); }
            }}
            className="sw-btn-primary"
          ><Download size={14} /> {t("org.export_pdf")}</button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
          <input
            data-testid="orgchart-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("org.search_placeholder")}
            className="sw-input pl-11 w-full"
          />
        </div>
        {view === "tree" && (
          <div className="inline-flex items-center gap-1.5 rounded-[10px] border border-[#cbd5e1] bg-white px-2 py-1 text-[12px] text-[#475569]" data-testid="orgchart-zoom">
            <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))} className="hover:text-[#1e293b]" aria-label={t("common.zoom_out")}><Minus size={14} /></button>
            <span className="w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(1.4, +(z + 0.1).toFixed(2)))} className="hover:text-[#1e293b]" aria-label={t("common.zoom_in")}><Plus size={14} /></button>
          </div>
        )}
      </div>

      {/* Tree view */}
      {view === "tree" && (
        <div className="sw-tree-scroll" data-testid="orgchart-tree">
          <div className="sw-tree" style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
            {renderTree({
              roots, byId, depts: data.departments, expandedMap, toggle,
              onSelect: setSelected, search: q, matchSet,
            })}
          </div>
        </div>
      )}

      {/* List view (legacy) */}
      {view === "list" && (
        <div className="flex flex-col gap-2" data-testid="orgchart-list">
          {flatList.map(({ node, depth, isExpanded, hasReports }) => {
            const initials = node.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
            const dept = data.departments.find((d) => d.id === node.department_id);
            return (
              <div key={node.id} className="flex items-stretch gap-2 relative" style={{ paddingLeft: `${depth * 28}px` }}>
                {depth > 0 && (
                  <div className="absolute top-0 bottom-0 flex items-center pointer-events-none" style={{ left: `${(depth - 1) * 28 + 12}px` }}>
                    <div className="w-px h-full bg-[#cbd5e1]" />
                  </div>
                )}
                <button
                  onClick={() => hasReports && toggle(node.id)}
                  data-testid={`node-toggle-${node.id}`}
                  className={`shrink-0 w-6 h-6 mt-3 rounded-[6px] flex items-center justify-center ${hasReports ? "bg-white border border-[#cbd5e1] hover:bg-[#e2e8f0]" : "bg-transparent"}`}
                >
                  {hasReports ? (isExpanded ? <ChevronDown size={14} className="text-[#475569]" /> : <ChevronRight size={14} className="text-[#475569]" />) : <span className="w-2 h-2 rounded-full bg-[#cbd5e1]" />}
                </button>
                <div
                  className="sw-card !p-3 flex items-center gap-3 flex-1 hover:border-[#475569] cursor-pointer hover:shadow-md transition-all"
                  data-testid={`node-${node.id}`}
                  onClick={() => setSelected(node)}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#475569] to-[#94a3b8] text-white flex items-center justify-center text-[12px] font-semibold shrink-0">{initials}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[14px] text-[#323232] truncate">{node.name}</div>
                    <div className="text-[12px] text-[#747474] truncate">{node.job_title || "—"}{dept && ` · ${dept.name}`}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="sw-badge sw-badge-gold capitalize hidden md:inline-flex !text-[10px]">{node.role}</span>
                    {hasReports && <span className="text-[11px] text-[#475569] font-semibold bg-[#e2e8f0] px-2 py-0.5 rounded-[6px]">{node._reportIds.length} {node._reportIds.length === 1 ? t("org.report_one") : t("org.report_many")}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6">
        <h2 className="sw-heading text-[18px] mb-4 flex items-center gap-2"><Building2 size={18} /> {t("org.departments_title")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.departments.map((d) => {
            const head = data.users.find((u) => u.id === d.manager_id);
            const count = data.users.filter((u) => u.department_id === d.id).length;
            return (
              <div key={d.id} className="sw-card">
                <div className="text-[11px] uppercase tracking-wider text-[#475569]">{d.cost_code}</div>
                <div className="font-medium text-[16px] text-[#323232] mt-1">{d.name}</div>
                <div className="text-[13px] text-[#747474] mt-1">
                  {head ? `${t("org.head")}: ${head.name}` : t("org.no_head")} · {count} {t("org.colab_short")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <EmployeeProfileModal
          user={selected}
          manager={data.users.find((u) => u.id === selected.manager_id)}
          department={data.departments.find((d) => d.id === selected.department_id)}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
